/**
 * Rasteriser: copies the canvas into Web Mercator raster tiles.
 *
 * The canvas grid *is* the Web Mercator quadtree (see @drawearth/geo), so this
 * is integer arithmetic rather than a reprojection. Canvas pixels land on
 * output pixels exactly, which is why they draw as true axis-aligned squares
 * with no rotation, shear or resampling.
 *
 * Three modes (see mercator.ts):
 *   full — real pixels, magnified or box-filtered from the canvas tiles
 *   tile — per-tile (~306 m) average colours from /v1/globe/tiles
 *   cell — per-cell (~1.2 km) average colours from /v1/globe/cells
 *
 * Runs in a Web Worker in production and on the main thread in tests.
 */
import { ACTIVITY_ZOOM, TILE_SIZE, TILE_ZOOM, WORLD_PIXELS, activityCellFromKey, activityCellRefCenter, tileFromKey, unproject } from '@drawearth/geo';
import { PALETTE_U32 } from '@drawearth/shared';
import { metersPerPixel, tilePixelLat } from './mercator.ts';

export { TILE_SIZE, TILE_ZOOM };

/** Most canvas pixels sampled per axis when shrinking; caps the work per tile. */
const MAX_BOX_SAMPLES = 4;

export interface TileDataProvider {
  /** undefined = not loaded (renders transparent), null = known empty tile. */
  get(key: number): Uint8Array | null | undefined;
}

/** Packed little-endian RGBA. */
export function packRGBA(r: number, g: number, b: number, a: number): number {
  return ((a << 24) | (b << 16) | (g << 8) | r) >>> 0;
}

/** Canvas tile key for a global canvas pixel. Mirrors geo's tileKey. */
function tileKeyAt(gx: number, gy: number): number {
  return Math.floor(gy / TILE_SIZE) * (1 << TILE_ZOOM) + Math.floor(gx / TILE_SIZE);
}

/**
 * Full-resolution render.
 *
 * At the canvas zoom and above, one canvas pixel fills a whole square block of
 * output pixels, so this is a block copy and pixels stay exactly square. Below
 * it, each output pixel box-filters the canvas pixels it covers.
 */
export function renderFull(z: number, x: number, y: number, size: number, provider: TileDataProvider): Uint8Array {
  const out = new Uint8Array(size * size * 4);
  const u32 = new Uint32Array(out.buffer);
  // Global canvas pixels per output pixel.
  const span = Math.pow(2, TILE_ZOOM - z) * (256 / size);
  const originX = x * size * span;
  const originY = y * size * span;
  if (originY >= WORLD_PIXELS || originY + size * span <= 0) return out;

  let lastKey = -1;
  let lastData: Uint8Array | null | undefined;
  const colourAt = (gx: number, gy: number): number => {
    if (gy < 0 || gy >= WORLD_PIXELS) return 0;
    const wx = ((gx % WORLD_PIXELS) + WORLD_PIXELS) % WORLD_PIXELS;
    const key = tileKeyAt(wx, gy);
    if (key !== lastKey) {
      lastKey = key;
      lastData = provider.get(key);
    }
    if (!lastData) return 0;
    return lastData[(gy % TILE_SIZE) * TILE_SIZE + (wx % TILE_SIZE)];
  };

  if (span <= 1) {
    // Magnifying: each output pixel reads the one canvas pixel beneath it.
    for (let oy = 0; oy < size; oy++) {
      const gy = Math.floor(originY + oy * span);
      const row = oy * size;
      for (let ox = 0; ox < size; ox++) {
        const colour = colourAt(Math.floor(originX + ox * span), gy);
        if (colour !== 0) u32[row + ox] = PALETTE_U32[colour];
      }
    }
    return out;
  }

  // Shrinking: average the canvas pixels under each output pixel so detail
  // survives instead of being dropped by point sampling.
  const step = Math.max(1, Math.floor(span / MAX_BOX_SAMPLES));
  for (let oy = 0; oy < size; oy++) {
    const gy0 = Math.floor(originY + oy * span);
    const gy1 = Math.floor(originY + (oy + 1) * span);
    const row = oy * size;
    for (let ox = 0; ox < size; ox++) {
      const gx0 = Math.floor(originX + ox * span);
      const gx1 = Math.floor(originX + (ox + 1) * span);
      let r = 0;
      let g = 0;
      let b = 0;
      let n = 0;
      let total = 0;
      for (let gy = gy0; gy < gy1; gy += step) {
        for (let gx = gx0; gx < gx1; gx += step) {
          total++;
          const colour = colourAt(gx, gy);
          if (colour === 0) continue;
          const packed = PALETTE_U32[colour];
          r += packed & 0xff;
          g += (packed >>> 8) & 0xff;
          b += (packed >>> 16) & 0xff;
          n++;
        }
      }
      if (n === 0) continue;
      // Alpha follows coverage, so sparse paint fades instead of blocking up.
      const a = Math.max(70, Math.round((255 * n) / Math.max(1, total)));
      u32[row + ox] = packRGBA(Math.round(r / n), Math.round(g / n), Math.round(b / n), a);
    }
  }
  return out;
}

export type SummaryLevel = 'tile' | 'cell';

/** Ground size of one summary square at the equator, for choosing sample vs stamp. */
const SUMMARY_METRES: Record<SummaryLevel, number> = { tile: 306, cell: 1223 };

function summaryCentre(key: number, level: SummaryLevel): { lat: number; lng: number } {
  if (level === 'cell') return activityCellRefCenter(activityCellFromKey(key));
  const t = tileFromKey(key);
  return unproject((t.x + 0.5) * TILE_SIZE, (t.y + 0.5) * TILE_SIZE);
}

/**
 * Low-zoom render: squares are smaller than a raster pixel, so stamp a soft dot
 * at each painted square's centre instead of sampling per pixel.
 */
export function stampSummary(z: number, x: number, y: number, size: number, level: SummaryLevel, colours: Map<number, number>, radiusPx = 4): Uint8Array {
  const out = new Uint8Array(size * size * 4);
  if (colours.size === 0) return out;
  const n = Math.pow(2, z);
  const west = (x / n) * 360 - 180;
  const spanLng = 360 / n;
  const north = tilePixelLat(z, y, -0.5, size);
  const south = tilePixelLat(z, y, size - 0.5, size);
  const margin = (radiusPx + 1) * (spanLng / size);
  const lngToPx = (lng: number) => ((lng - west) / spanLng) * size;
  const latToPy = (lat: number) => {
    const r = (lat * Math.PI) / 180;
    const m = Math.log(Math.tan(Math.PI / 4 + r / 2));
    const mTop = Math.log(Math.tan(Math.PI / 4 + (north * Math.PI) / 360));
    const mBot = Math.log(Math.tan(Math.PI / 4 + (south * Math.PI) / 360));
    return ((mTop - m) / (mTop - mBot)) * size;
  };
  for (const [key, packed] of colours) {
    const c = summaryCentre(key, level);
    if (c.lng < west - margin || c.lng > west + spanLng + margin) continue;
    if (c.lat > north + 2 || c.lat < south - 2) continue;
    const px = lngToPx(c.lng);
    const py = latToPy(c.lat);
    if (px < -radiusPx - 1 || px > size + radiusPx + 1 || py < -radiusPx - 1 || py > size + radiusPx + 1) continue;
    const r = packed & 0xff;
    const g = (packed >>> 8) & 0xff;
    const b = (packed >>> 16) & 0xff;
    for (let dy = -radiusPx; dy <= radiusPx; dy++) {
      for (let dx = -radiusPx; dx <= radiusPx; dx++) {
        const d = Math.sqrt(dx * dx + dy * dy) / radiusPx;
        if (d > 1) continue;
        const ix = Math.round(px + dx);
        const iy = Math.round(py + dy);
        if (ix < 0 || iy < 0 || ix >= size || iy >= size) continue;
        const o = (iy * size + ix) * 4;
        const a = Math.round(255 * (1 - d * d * 0.85));
        if (out[o + 3] >= a) continue;
        out[o] = r;
        out[o + 1] = g;
        out[o + 2] = b;
        out[o + 3] = a;
      }
    }
  }
  return out;
}

/** Summary render from per-tile or per-cell colours keyed numerically. */
export function renderSummary(z: number, x: number, y: number, size: number, level: SummaryLevel, colours: Map<number, number>): Uint8Array {
  const out = new Uint8Array(size * size * 4);
  const u32 = new Uint32Array(out.buffer);
  if (colours.size === 0) return out;
  // When a square covers less than ~3 raster pixels, stamp dots so painted places stay visible.
  const midLat = tilePixelLat(z, y, size / 2, size);
  if (SUMMARY_METRES[level] / metersPerPixel(z, midLat, size) < 3) return stampSummary(z, x, y, size, level, colours);
  // Summary squares are quadtree cells too, so this is the same integer walk —
  // but the unit here is one summary square, not one canvas pixel.
  const gridZoom = level === 'tile' ? TILE_ZOOM : ACTIVITY_ZOOM;
  const squaresPerTile = Math.pow(2, gridZoom - z);
  const span = squaresPerTile / size;
  const originX = x * squaresPerTile;
  const originY = y * squaresPerTile;
  const world = 1 << gridZoom;
  for (let oy = 0; oy < size; oy++) {
    const gy = Math.floor(originY + oy * span);
    if (gy < 0 || gy >= world) continue;
    const row = oy * size;
    let lastKey = -1;
    let lastColour = 0;
    for (let ox = 0; ox < size; ox++) {
      const gx = ((Math.floor(originX + ox * span) % world) + world) % world;
      const key = gy * world + gx;
      if (key !== lastKey) {
        lastKey = key;
        lastColour = colours.get(key) ?? 0;
      }
      if (lastColour) u32[row + ox] = lastColour;
    }
  }
  return out;
}
